import { useCallback, useEffect, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import {
  KAPCSOLAT_CELOK,
  KAPCSOLAT_CEL_NEVEK,
  api,
  type EtteremReszletek as Reszletek,
  type KapcsolatCel,
} from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { ft } from '../formazas.ts';
import { Mezo, SzamMezo, ValasztoMezo } from './Mezo.tsx';

const ADOSZAM_MINTA = /^\d{8}-\d-\d{2}$/;

/**
 * Egy étterem részletes szerkesztője — minden mezővel, amit a táblázat nem mutat.
 * A névre kattintva nyílik, és ugyanúgy mezőnként ment.
 */
export function EtteremReszletek({
  id,
  onBezar,
  onValtozott,
  onKarton,
}: {
  id: number;
  onBezar: () => void;
  onValtozott: () => void;
  onKarton: (etterem_id: number) => void;
}) {
  /*
   * A partner adatlapján az **ár**, az **elérhetőség** és minden más külön
   * engedély. Aki csak nézni jött, annál a mezők nem szerkeszthetők. (3.0 / 1.)
   */
  const arJog = useMuvelet('partner_ar');
  const modJog = useMuvelet('partner_mod');
  const alapadatok = useAlapadatok();
  const [e, setE] = useState<Reszletek | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [ujTelefon, setUjTelefon] = useState('');
  const [ujEmail, setUjEmail] = useState('');
  const [ujCel, setUjCel] = useState<KapcsolatCel>('egyeb');

  const betolt = useCallback(async () => {
    try {
      setE(await api.get<Reszletek>(`/api/etterem/${id}`));
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni.');
    }
  }, [id]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const ment = (mezo: string) => async (ertek: string) => {
    const frissitett = await api.patch<Reszletek>(`/api/etterem/${id}/mezo/${mezo}`, { ertek });
    setE(frissitett);
    onValtozott();
  };

  const jelolo = (mezo: string) => async (bekapcsolva: boolean) => {
    const frissitett = await api.patch<Reszletek>(`/api/etterem/${id}/mezo/${mezo}`, {
      ertek: bekapcsolva,
    });
    setE(frissitett);
    onValtozott();
  };

  if (hiba) return <div className="uzenet bad">{hiba}</div>;
  if (!e) return <div className="tdet halvany">Betöltés…</div>;

  const vanAr = (e.netto_ar ?? 0) > 0 || (e.brutto_ar ?? 0) > 0;

  return (
    <div className="tdet">
      <h4>
        {e.nev}
        {vanAr ? (
          <span className="halvany"> — {ft(e.brutto_ar)} Ft/l bruttó</span>
        ) : (
          <span className="only"> — csak gyűjtés</span>
        )}
        {e.telephelyek > 1 && (
          <span className="halvany"> · {e.telephelyek} telephelyes cég</span>
        )}
      </h4>

      <div className="g4">
        <Cimke szoveg="Megnevezés">
          <Mezo ertek={e.nev} onMent={ment('nev')} tiltva={!modJog} />
        </Cimke>
        <Cimke szoveg="Cégcsoport">
          <Mezo ertek={e.cegcsoport} onMent={ment('cegcsoport')} tiltva={!modJog} />
        </Cimke>
        <Cimke szoveg="Irányítószám">
          <Mezo ertek={e.irsz} onMent={ment('irsz')} tiltva={!modJog} />
        </Cimke>
        <Cimke szoveg="Cím">
          <Mezo ertek={e.cim} onMent={ment('cim')} tiltva={!modJog} />
        </Cimke>

        <Cimke szoveg="Helység">
          <Mezo ertek={e.helyseg} onMent={ment('helyseg')} tiltva={!modJog} />
        </Cimke>
        <Cimke szoveg={`Nettó ár (bruttó: ${ft(e.brutto_ar)})`}>
          <SzamMezo ertek={e.netto_ar} onMent={ment('netto_ar')} tiltva={!arJog} />
        </Cimke>
        <Cimke szoveg={`F+ nettó (bruttó: ${ft(e.fplusz_brutto)})`}>
          <SzamMezo ertek={e.fplusz_netto} onMent={ment('fplusz_netto')} tiltva={!arJog} />
        </Cimke>
        <Cimke szoveg="Fizetési mód">
          <ValasztoMezo
            ertek={e.fizetesi_mod}
            uresCimke="Készpénz"
            lehetosegek={alapadatok.fizetesi_modok
              .filter((m) => m.kod !== 'KP')
              .map((m) => ({ ertek: m.kod, cimke: m.nev }))}
            onMent={ment('fizetesi_mod')}
              tiltva={!modJog}
          />
        </Cimke>

        <Cimke szoveg="MO ár (moslék)">
          <SzamMezo ertek={e.mo_ar} onMent={ment('mo_ar')} tiltva={!arJog} />
        </Cimke>
        <Cimke szoveg="R ár (raktári)">
          <SzamMezo ertek={e.r_ar} onMent={ment('r_ar')} tiltva={!arJog} />
        </Cimke>
        <Cimke szoveg="FO gyűjtő">
          <Mezo ertek={e.fo_gyujto} onMent={ment('fo_gyujto')} tiltva={!modJog} />
        </Cimke>
        <Cimke szoveg="Megjegyzés">
          <Mezo ertek={e.megjegyzes} onMent={ment('megjegyzes')} tiltva={!modJog} />
        </Cimke>

        <Cimke szoveg="Cégnév">
          <Mezo ertek={e.cegnev} onMent={ment('cegnev')} tiltva={!modJog} />
        </Cimke>
        <Cimke szoveg="Adószám">
          <Mezo
            ertek={e.adoszam}
            onMent={ment('adoszam')}
              tiltva={!modJog}
            helykitolto="12345678-2-42"
            ellenoriz={(v) =>
              !v || ADOSZAM_MINTA.test(v) ? null : 'Várt alak: 12345678-2-42'
            }
          />
        </Cimke>
        <Cimke szoveg="Székhely">
          <Mezo ertek={e.szekhely} onMent={ment('szekhely')} tiltva={!modJog} />
        </Cimke>
        <div />
      </div>

      {/* --- jelölők --- */}
      <div className="btns" style={{ marginTop: 12, gap: 18 }}>
        <label className="sw">
          <input
            type="checkbox"
            checked={e.csak_gyujtes}
            disabled={!modJog}
            onChange={(ev) => void jelolo('csak_gyujtes')(ev.target.checked)}
          />
          csak gyűjtés (nem adunk el olajat)
        </label>
        <label className="sw">
          <input
            type="checkbox"
            checked={e.mo_gyujtes}
            disabled={!modJog}
            onChange={(ev) => void jelolo('mo_gyujtes')(ev.target.checked)}
          />
          moslékot gyűjtünk innen
        </label>
        <label className="sw">
          <input
            type="checkbox"
            checked={e.aktiv}
            disabled={!modJog}
            onChange={(ev) => void jelolo('aktiv')(ev.target.checked)}
          />
          aktív
        </label>
      </div>

      {/* --- elérhetőségek --- */}
      <div className="elerhetosegek">
        <Elerhetoseg
          cim="Telefonszámok"
          tetelek={e.telefonok}
          ujErtek={ujTelefon}
          setUjErtek={setUjTelefon}
          onFelvesz={async (ertek) => {
            setE(await api.post<Reszletek>(`/api/etterem/${id}/kapcsolat`, {
              tipus: 'telefon',
              ertek,
            }));
            setUjTelefon('');
          }}
          onModosit={async (kid, ertek) => {
            setE(await api.patch<Reszletek>(`/api/etterem/kapcsolat/${kid}`, { ertek }));
          }}
          onTorol={async (kid) => {
            setE(await api.torol<Reszletek>(`/api/etterem/kapcsolat/${kid}`));
          }}
        />
        <Elerhetoseg
          cim="E-mail címek"
          tetelek={e.emailek}
          ujErtek={ujEmail}
          setUjErtek={setUjEmail}
          /* A kategória mondja meg, melyik levél melyik címre menjen. */
          ujCel={ujCel}
          setUjCel={setUjCel}
          onFelvesz={async (ertek) => {
            setE(await api.post<Reszletek>(`/api/etterem/${id}/kapcsolat`, {
              tipus: 'email',
              ertek,
              cel: ujCel,
            }));
            setUjEmail('');
            setUjCel('egyeb');
          }}
          onModosit={async (kid, ertek) => {
            setE(await api.patch<Reszletek>(`/api/etterem/kapcsolat/${kid}`, { ertek }));
          }}
          onCel={async (kid, cel) => {
            setE(await api.patch<Reszletek>(`/api/etterem/kapcsolat/${kid}`, { cel }));
          }}
          onTorol={async (kid) => {
            setE(await api.torol<Reszletek>(`/api/etterem/kapcsolat/${kid}`));
          }}
        />
      </div>

      <div className="btns">
        <button className="btn pri sm" onClick={onBezar}>
          Kész
        </button>
        <button className="btn sm" onClick={() => onKarton(id)}>
          Partnerkarton megnyitása
        </button>
        <span className="hint">
          Minden módosítás azonnal mentődik. Nincs külön mentés gomb.
        </span>
      </div>
    </div>
  );
}

function Cimke({ szoveg, children }: { szoveg: string; children: React.ReactNode }) {
  return (
    <div>
      <label>{szoveg}</label>
      {children}
    </div>
  );
}

function Elerhetoseg({
  cim,
  tetelek,
  ujErtek,
  setUjErtek,
  ujCel,
  setUjCel,
  onFelvesz,
  onModosit,
  onCel,
  onTorol,
}: {
  cim: string;
  tetelek: Array<{ id: number; ertek: string; cel?: KapcsolatCel }>;
  ujErtek: string;
  setUjErtek: (s: string) => void;
  /* Csak az e-mail listánál van kategória; a telefonnál ezek hiányoznak. */
  ujCel?: KapcsolatCel;
  setUjCel?: (c: KapcsolatCel) => void;
  onFelvesz: (ertek: string) => Promise<void>;
  onModosit: (kid: number, ertek: string) => Promise<void>;
  onCel?: (kid: number, cel: KapcsolatCel) => Promise<void>;
  onTorol: (kid: number) => Promise<void>;
}) {
  const celes = !!onCel && !!setUjCel;
  return (
    <div>
      <label>{cim}</label>
      {tetelek.map((t) => (
        <div key={t.id} className="elerhetosor">
          <Mezo ertek={t.ertek} onMent={(uj) => onModosit(t.id, uj)} />
          {celes && (
            <select
              className="celvalaszto"
              value={t.cel ?? 'egyeb'}
              title="Mire jó ez a cím"
              onChange={(ev) => void onCel(t.id, ev.target.value as KapcsolatCel)}
            >
              {KAPCSOLAT_CELOK.map((c) => (
                <option key={c} value={c}>
                  {KAPCSOLAT_CEL_NEVEK[c]}
                </option>
              ))}
            </select>
          )}
          <button className="xb" title="Törlés" onClick={() => void onTorol(t.id)}>
            ×
          </button>
        </div>
      ))}
      <div className="elerhetosor">
        <input
          value={ujErtek}
          placeholder="+ új…"
          onChange={(ev) => setUjErtek(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' && ujErtek.trim()) void onFelvesz(ujErtek.trim());
          }}
          /* Kilépéskor is mentünk — lásd ElerhetosegDoboz. (2026-09-28) */
          onBlur={() => {
            if (ujErtek.trim()) void onFelvesz(ujErtek.trim());
          }}
        />
        {celes && (
          <select
            className="celvalaszto"
            value={ujCel}
            title="Mire jó ez a cím"
            onChange={(ev) => setUjCel(ev.target.value as KapcsolatCel)}
          >
            {KAPCSOLAT_CELOK.map((c) => (
              <option key={c} value={c}>
                {KAPCSOLAT_CEL_NEVEK[c]}
              </option>
            ))}
          </select>
        )}
        <button
          className="xb"
          title="Felvétel"
          disabled={!ujErtek.trim()}
          onClick={() => ujErtek.trim() && void onFelvesz(ujErtek.trim())}
        >
          +
        </button>
      </div>
      {celes && (
        <div className="hint">
          A szállítólevél, a számla és a felszólítás a hozzá tartozó címre megy. Ha valamelyikhez
          nincs külön cím, az „egyéb" kapja.
        </div>
      )}
    </div>
  );
}
